import SimpleStorage from '../utils/simpleStorage';
import { appointmentManager, type Appointment } from '../utils/appointmentManager';
import type { BusinessOwner } from '../utils/authManager';
import CALENDAR_CONFIG from '../config/calendarConfig';
import { activeCalendarIntegrationService } from './activeCalendarIntegrationService';

function syncedEventsStorageKey(): string {
  return CALENDAR_CONFIG.USE_REAL_CALENDAR_API ? 'real_synced_events' : 'synced_events';
}

/** True if this appointment id already has a recorded calendar export (any connected calendar). */
export async function isAppointmentAlreadySyncedToCalendar(appointmentId: string): Promise<boolean> {
  try {
    const raw = await SimpleStorage.getItem(syncedEventsStorageKey());
    if (!raw) return false;
    const map = JSON.parse(raw) as Record<string, unknown>;
    return !!map[appointmentId];
  } catch {
    return false;
  }
}

function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Normalize stored appointment time (12h or 24h) to HH:mm for calendar APIs. */
export function normalizeTimeForCalendar(appointmentTime: string | undefined): string {
  const t = (appointmentTime || '09:00').trim();
  const ampm = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = ampm[2];
    const mer = ampm[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${m}`;
  }
  const hm = t.match(/^(\d{1,2}):(\d{2})$/);
  if (hm) {
    const h = Math.min(23, Math.max(0, parseInt(hm[1], 10)));
    return `${String(h).padStart(2, '0')}:${hm[2]}`;
  }
  return '09:00';
}

function addressLine(owner: BusinessOwner): string {
  const a = owner.address as any;
  if (!a) return '';
  if (typeof a === 'string') return a;
  if (a.street && (a.street.includes(',') || String(a.street).length > 25)) return a.street;
  return [a.street, a.city, a.state, a.zipcode, a.country].filter(Boolean).join(', ');
}

const SKIP_STATUSES: Appointment['status'][] = ['cancelled', 'declined', 'expired'];

export function buildCalendarEventPayload(apt: Appointment, businessOwner: BusinessOwner) {
  const timeNorm = normalizeTimeForCalendar(apt.appointmentTime);
  const duration =
    typeof apt.estimatedDurationMinutes === 'number' && apt.estimatedDurationMinutes > 0
      ? apt.estimatedDurationMinutes
      : businessOwner.appointmentDuration || 30;
  return {
    id: apt.id,
    serviceName: apt.serviceType || 'Appointment',
    customerName: apt.customerName || 'Customer',
    appointmentDate: apt.appointmentDate,
    appointmentTime: timeNorm,
    duration,
    businessName: businessOwner.companyName || businessOwner.name || 'Business',
    businessAddress: addressLine(businessOwner),
    businessPhone: businessOwner.phone || '',
    notes: apt.specialInstructions || undefined,
  };
}

/**
 * Adds one appointment to every calendar connected on this device (skips if already exported).
 */
export async function createCalendarEventsForConnectedCalendars(
  apt: Appointment,
  businessOwner: BusinessOwner
): Promise<void> {
  if (SKIP_STATUSES.includes(apt.status)) return;
  if (await isAppointmentAlreadySyncedToCalendar(apt.id)) return;
  const accounts = (await activeCalendarIntegrationService.getConnectedAccounts()).filter(a => a.isConnected);
  if (accounts.length === 0) return;
  const payload = buildCalendarEventPayload(apt, businessOwner);
  for (const acc of accounts) {
    try {
      const r = await activeCalendarIntegrationService.createCalendarEvent(payload, acc.id);
      if (!r.success && r.error) {
        console.warn('[BookAPT] Calendar sync:', acc.type, r.error);
      }
    } catch (e: any) {
      console.warn('[BookAPT] Calendar sync failed:', acc.type, e?.message || e);
    }
  }
}

/**
 * Pushes upcoming appointments into every calendar the owner has connected on this device.
 * May create duplicate events if run repeatedly (no remote de-duplication yet).
 */
export async function syncUpcomingBusinessAppointmentsToCalendars(
  businessOwner: BusinessOwner,
  options?: { daysAhead?: number }
): Promise<{ created: number; skipped: number; errors: string[] }> {
  const errors: string[] = [];
  const accounts = await activeCalendarIntegrationService.getConnectedAccounts();
  const activeAccounts = accounts.filter(a => a.isConnected);
  if (activeAccounts.length === 0) {
    return {
      created: 0,
      skipped: 0,
      errors: ['Connect a calendar first (Google, Outlook, or Apple).'],
    };
  }

  const id = businessOwner.id;
  if (!id) {
    return { created: 0, skipped: 0, errors: ['Business profile is missing an id.'] };
  }

  const days = options?.daysAhead ?? 21;
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + days);
  const startStr = formatYMD(start);
  const endStr = formatYMD(end);

  let list: Appointment[] = [];
  try {
    list = await appointmentManager.getAppointmentsByDateRange(id, startStr, endStr, { limit: 250 });
  } catch (e: any) {
    return { created: 0, skipped: 0, errors: [e?.message || 'Could not load appointments.'] };
  }

  let created = 0;
  let skipped = 0;

  for (const apt of list) {
    if (SKIP_STATUSES.includes(apt.status)) {
      skipped += 1;
      continue;
    }

    const payload = buildCalendarEventPayload(apt, businessOwner);

    for (const acc of activeAccounts) {
      try {
        const r = await activeCalendarIntegrationService.createCalendarEvent(payload, acc.id);
        if (r.success) created += 1;
        else if (r.error) errors.push(`${apt.serviceType || 'Visit'} ${apt.appointmentDate} (${acc.type}): ${r.error}`);
      } catch (e: any) {
        errors.push(`${apt.id} (${acc.type}): ${e?.message || String(e)}`);
      }
    }
  }

  return { created, skipped, errors };
}
